import { InstancePlugin, Tooltip } from "@bryntum/schedulerpro";
import { getDatesFromSelectedEventTick } from "../../other/utils/utils";
import { PLUGINS_KEYS } from "../../other/constants/PluginKeys.jsx";
import { swivuSortAscendingKey } from "../../other/utils/otherUtils.jsx";

const timelineFieldId = "timelineField";

export default class SwivuSchedulerTimelineSortPlugin extends InstancePlugin {
  static get $name() {
    return PLUGINS_KEYS.timelineEventSort;
  }

  construct(scheduler) {
    super.construct(...arguments);
    if (!scheduler.isConstructing) {
      this.init();
    }
  }

  static get pluginConfig() {
    return {
      chain: ["populateTimeAxisHeaderMenu"]
    };
  }

  init() {
    this.lastStartDate = null;
    this.lastEndDate = null;

    // this array holds listeners, that should be called on sort change
    this.sortListeners = [];

    /**
     * @type {SortDirs} sortDir
     */
    this.sortDir;

    this.destroySortEventListener;
    this.loading = true;
    this.scheduler = this.client;
    this.listenSortEventOnStore();
    this.loading = false;

    const tooltipInstance = this.initializeSortTooltipWidget();

    const detacher = this.scheduler.on({
      timeAxisHeaderClick: this.applySorterOnTimeAxisHeaderClick.bind(this),
      beforeDestroy: () => {
        tooltipInstance.destroy();
        detacher(); // On widget destroy, remove listener
      }
    });
  }

  populateTimeAxisHeaderMenu({ items }) {
    items.sortAscending = {
      text: "L{Sort.sortAscending}",
      icon: "b-icon b-icon-sort-asc",
      onItem: ({ menu }) => {
        const dates = getDatesFromSelectedEventTick(menu);
        this.sort(dates, "asc");
      }
    };
    items.sortDescending = {
      text: "L{Sort.sortDescending}",
      icon: "b-icon b-icon-sort-desc",
      onItem: ({ menu }) => {
        const dates = getDatesFromSelectedEventTick(menu);
        this.sort(dates, "desc");
      }
    };
  }

  async applySorterOnTimeAxisHeaderClick({ startDate, endDate, event }) {
    // this.sort applies a hidden sort on resource store which dismisses the custom view. Since the sort is hidden therefore that sort is not applied to the mapped default view.
    // By manually reapplying sort creates a glitchy flicker effect because therefore dismissing view first and then applying this.sort.
    // Todo: Handle view dismissal from ViewViewStateStorage.
    const { viewManager, project, store, resourceStore } = this.scheduler;
    if (!viewManager.renderState.selectedView.isDefaultView) {
      viewManager.selectDefaultViewAndMapState(
        viewManager.renderState.selectedView.viewState
      );
    }
    const promise = project.commitAsync();
    this.sort(
      { startDate, endDate },
      event.shiftKey ? "asc" : "desc"
    );
    store.beginBatch();
    resourceStore.removeSorter();
    // the hidden archive sort on Resource trigger event sort order to reset, in order prevent this behavior we need the following patch
    store.forEach((resource, i) => (resource.lastOrderKey = i));
    store.sort({
      fn: (a, b) => a.lastOrderKey - b.lastOrderKey,
      hidden: true
    });
    store.endBatch();
    this.clearSort();
    await promise;
  }

  initializeSortTooltipWidget() {
    const dispatchShiftClickEvent = (tooltip, shiftKey) => {
      const { activeTarget } = tooltip;
      const mouseEvent = new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
        shiftKey
      });
      activeTarget.dispatchEvent(mouseEvent);
    }

    const tooltipInstance = new Tooltip({
      forSelector: ".b-sch-header-timeaxis-cell",
      hideOnDelegateChange: true,
      allowOver: true,
      anchorToTarget: true,
      contentElementCls: "p-[0.25em] gap-[0.25em] dark:bg-[#2A2B2D]",
      align: {
        offset: [0, -8]
      },
      items: {
        sortDesc: {
          type: "button",
          cls: "min-h-0 p-1 border-0 text-xl text-black",
          icon: "fa-thin fa-grid-dividers swivu-icon",
          onClick: () => {
            dispatchShiftClickEvent(tooltipInstance, false);
          }
        },
        sortAsc: {
          type: "button",
          cls: "min-h-0 p-1 border-0 text-xl text-black",
          icon: "fa-solid fa-grid-dividers",
          onClick: () => {
            dispatchShiftClickEvent(tooltipInstance, true);
          }
        }
      },
    })

    return tooltipInstance;
  }

  doDisable(disable) {
    super.doDisable(disable);
  }

  listenSortEventOnStore() {
    // if there's previously sets the below one listener then remove that first
    this.destroySortEventListener?.();

    const scStore = this.scheduler.store;

    this.destroySortEventListener = scStore.on("sort", ({ sorters }) => {
      if (!this.loading && this.sortDir) {
        // In case of from somewhere else the timeline sorter being removed then we must update this instance as well accordingly.
        const timelineSorter = sorters.find(
          (sorter) => sorter.swivuFieldId === timelineFieldId
        );

        if (!timelineSorter) {
          // if there's not timeline sorter then remove the sort
          this.sortDir = null; // this should be set to null first, otherwise recursive call to this function will happen
          this.clearSort();
        }
      }
    });
  }

  getResourceByTaskNameSorter(startDate, endDate, ascending = false) {
    return (a, b) => {
      if (a.isGroupHeader || b.isGroupHeader) return 0;
      // DO not change order by default
      let result = 0;

      const aTasks = [],
        bTasks = [];

      // Iterate cached assignments and look for associated events and tasks.
      // TODO: This should be cached by the record/store. Cache should be updated every time
      // assignment or event changes. Potentially this could save a lot of computing time. But
      // for prototyping purposes it is ok to iterate every time
      [
        [a, aTasks],
        [b, bTasks]
      ].forEach(([record, tasks]) => {
        record.assignments.forEach(({ event }) => {
          // Check if record is joined to the store - this function might be called before project is committed and assignments are updated
          // Check if current event is within the target interval
          // Softly handling the error of event undefined issue
          // Todo: our understanding is that backend needs not to send archived records, assignments data. So this soft error handling needs to be removed once this has been resolved
          try {
            if (
              event.firstStore &&
              event.startDate.valueOf() < endDate.valueOf() &&
              event.endDate.valueOf() > startDate.valueOf() &&
              !tasks.includes(event.task.name)
            ) {
              tasks.push(event.task.name);
            }
          } catch (err) {
            // Note the error "Cannot read properties of null (reading 'firstStore')" will be thrown when there's an undefined `event` found, which most probably happen due to archiving feature
            console.log(err);
          }
        });
      });

      if (aTasks.length === 0) {
        // Both resources do not have any assignments in the given interval, sort them ascending
        if (bTasks.length === 0) {
          result = a.name?.localeCompare(b.name) || -1;
        }
        // b has some assignments and a does not, put a after b
        else {
          result = 1;
        }
      }
      // a has some assignments and b does not, put a before b
      else if (bTasks.length === 0) {
        result = -1;
      }
      // both a and b have some tasks, put them in task alphabetical order
      else {
        // Sort tasks by name, compare first item from array
        aTasks.sort();
        bTasks.sort();

        result = aTasks[0].localeCompare(bTasks[0]);

        // if tasks have the same name, put resources in alphabetical order
        if (result === 0) {
          result = a.name?.localeCompare(b.name) || -1;
        }
      }

      return result * (ascending ? 1 : -1);
    };
  }

  getOverlappingEventsSorter(startDate, endDate, ascending = false) {
    const start = startDate.valueOf();
    const end = endDate.valueOf();

    return (a, b) => {
      let result;

      const aStartDate = a.startDate.valueOf(),
        aEndDate = a.endDate.valueOf(),
        bStartDate = b.startDate.valueOf(),
        bEndDate = b.endDate.valueOf();

      // Check if record is joined to the store - this function might be called before project is committed and assignments are updated
      if (!a.firstStore || !b.firstStore) {
        return 0;
      }
      // If both events fall into same timeframe and start at the same time - apply custom sorter
      else if (
        aStartDate - bStartDate === 0 && // It appears that first condition could save us plenty of condition evaluation
        aStartDate < end &&
        aEndDate >= start &&
        bStartDate < end &&
        bEndDate >= start
      ) {
        result =
          (a.task.name || "").localeCompare(b.task.name || "") *
          (ascending ? 1 : -1);
      } else {
        if (aStartDate - bStartDate === 0) {
          result = 0;
        } else {
          result = aStartDate < bStartDate ? -1 : 1;
        }
      }

      return result;
    };
  }

  notifyListeners() {
    this.sortListeners.forEach((listener) => {
      listener(this.sortDir);
    });
  }

  /**
   * @param {{startDate: string|Date, endDate: string|Date}} dates
   * @param {SortDirs} dir
   * */
  sort(dates = {}, dir = "asc") {
    if (!this.scheduler || this.scheduler.isDestroying) return;
    let { startDate = this.lastStartDate, endDate = this.lastEndDate } = dates;

    if (!(this.scheduler && dir && startDate && endDate)) return;

    startDate = typeof startDate === "string" ? new Date(startDate) : startDate;
    endDate = typeof endDate === "string" ? new Date(endDate) : endDate;

    // we need the last start and end dates since, when clearing or reApplying the sort this has to be available
    this.lastStartDate = startDate;
    this.lastEndDate = endDate;

    const isAscending = dir === "asc";

    // Changing overlapping event sorter would refresh the view, as well as sorting the
    // event store. Therefore to avoid multiple renders we will suspend refresh for a time being
    this.scheduler.suspendRefresh();

    this.scheduler.overlappingEventSorter = this.getOverlappingEventsSorter(
      startDate,
      endDate,
      isAscending
    );
    this.scheduler.store.sort({
      swivuFieldId: timelineFieldId,
      // we were using this custom key to display order in view management, but since we no longer storing the event sort in viewstate this is not necessary now. keeping it for reference.
      [swivuSortAscendingKey]: isAscending, // by default, for custom sorter like this, if we try to assign value to ascending property then custom sorter fn will never be called
      fn: this.getResourceByTaskNameSorter(startDate, endDate, isAscending),
      hidden: true
    });

    this.scheduler.resumeRefresh(true);
    // Highlighting sort in scheduler date headers
    this.scheduler.timeRangeStore
      .find((item) => item.recordType === "eventSort")
      ?.remove();
    this.scheduler.timeRangeStore.add({
      startDate,
      endDate,
      cls: "bg-time-event",
      recordType: "eventSort"
    });

    this.sortDir = dir;
    this.notifyListeners();
  }

  clearSort() {
    if (!this.scheduler || this.scheduler.isDestroying) return;

    this.scheduler.timeRangeStore
      .find((item) => item.recordType === "eventSort")
      ?.remove();
    this.sortDir = undefined;
    this.notifyListeners();
    this.lastStartDate = null;
    this.lastEndDate = null;
  }

  /* This function is used in CalenderUtils */
  getSwivuSortState() {
    if (this.sortDir && this.lastStartDate && this.lastEndDate) {
      return {
        sortDir: this.sortDir,
        lastStartDate: this.lastStartDate,
        lastEndDate: this.lastEndDate
      };
    }
  }
}
